import { createStore } from '@stencil/store';
import { siteType } from '@pes-ui/utils';
import { products, additionalDistributors } from '@pes-ui/components/dist/collection/components/commerce-connector/__mock__/mocks';
import type { TCustomPrice } from '@pes-ui/utils';
import type { PesNavigationType, PesPageType, TSiteType } from '../../../../globals/gloabal-types';

const APP_NAME = 'pesAppTest';

export declare type PropKeys = keyof ApplicationSettingsInterface;

export declare type AppType = 'pesCatalog' | 'commerceConnector';

export declare type DirType = 'ltr' | 'rtl' | 'auto';

export interface ApplicationSettingsInterface {
  appType: AppType;
  host: string;
  endpoint: string;
  base_dir: string;
  siteType: TSiteType;
  showCustomPrice: boolean;
  showCatalogPrice: boolean;
  customPrice: TCustomPrice;
  returnBreadcrumbs: (list: unknown[]) => void;
  showBreadcrumbs: boolean;
  showLinks: boolean;
  showBuyOnline: boolean;
  showCompare: boolean;
  showRecentlyViewed: boolean;
  showFaq: boolean;
  language: string;
  country: string;
  page: PesPageType;
  pageId: string;
  usePesRouter: boolean;
  pesRouterNavigation: PesNavigationType;
  showDiscountedPrice: boolean;
  showCAASContentSlot: boolean;
  showDocuments: boolean;
  dir: string;
  products: string;
  additionalDistributors: string;
  substitutionLabelShort: boolean;
  searchParams: string;
  ddcAppSource: string;
  externalHost: string;
  ddcAllProductsUrl: string;
  ddcUserProfile: string;
  ddcAccessToken: string;
}

export const getSettings = (): string | null => localStorage.getItem(APP_NAME);

const DEFAULT_SETTINGS: ApplicationSettingsInterface = {
  appType: 'pesCatalog',
  host: 'https://www.se.com/',
  endpoint: 'product/',
  base_dir: 'catalogBrowse',
  siteType: siteType.B2B,
  showCustomPrice: false,
  showCatalogPrice: true,
  customPrice: {},
  returnBreadcrumbs: (list: unknown[]) => {
    // eslint-disable-next-line no-console
    console.log(JSON.stringify(list));
  },
  showBreadcrumbs: true,
  showBuyOnline: false,
  showLinks: true,
  showCompare: false,
  showRecentlyViewed: false,
  showFaq: false,
  language: 'en',
  country: 'us',
  page: '',
  pageId: '',
  usePesRouter: true,
  pesRouterNavigation: 'path',
  showDiscountedPrice: true,
  showCAASContentSlot: true,
  showDocuments: true,
  dir: 'ltr',
  products,
  additionalDistributors,
  substitutionLabelShort: false,
  searchParams: '{}',
  ddcAppSource: 'PES_MS_SE',
  externalHost: 'https://www.se.com/',
  ddcAllProductsUrl: '',
  ddcUserProfile: '{ "isAlive": true }',
  ddcAccessToken: 'IamTheToken',
};

const restoreSettings = (): ApplicationSettingsInterface => {
  const settings = getSettings();
  if (!settings) {
    return DEFAULT_SETTINGS;
  }

  return JSON.parse(settings);
};

const getDefaultState = () => (window.location.search.indexOf('use-defaults=true') !== -1
  ? DEFAULT_SETTINGS
  : {
    ...DEFAULT_SETTINGS,
    ...restoreSettings(),
  });

const defaultState = getDefaultState();

export const PBSStore = createStore<ApplicationSettingsInterface>(defaultState);

export const saveSettings = (): void => {
  if (typeof window.Proxy === 'function') {
    localStorage.setItem(APP_NAME, JSON.stringify(PBSStore.state));
  } else {
    // IE doesn't support Proxy so to save settings this hack uses
    const objToSave = {};

    Object.keys(DEFAULT_SETTINGS).forEach((key) => {
      // eslint-disable-next-line
      // @ts-ignore
      objToSave[key] = PBSStore.get(key);
    });

    localStorage.setItem(APP_NAME, JSON.stringify(objToSave));
  }
};

export const updateStorage = (data: string): void => {
  localStorage.setItem(APP_NAME, data);
};

PBSStore.on('set', saveSettings);
